import { useEffect, useState } from "react";
import { formatWib } from "@po/shared";
import { api, lastActivity, type Freshness, type Me } from "../lib/api";

/** Permanent ribbon on staging (SPEC §11.4). */
export function StagingRibbon() {
  return (
    <div className="bg-amber-400 text-black text-center text-[12px] font-bold tracking-wide py-0.5">
      STAGING — test data frozen ~14 Oct 2026
    </div>
  );
}

/** Severely stale: a red banner on every tab that can't be dismissed. Data stays visible (SPEC §11.2). */
export function Banners({ freshness: f }: { freshness?: Freshness }) {
  if (!f) return null;
  return (
    <>
      {f.level === "severe" && (
        <div role="alert" className="bg-red-700 text-white text-[13px] font-semibold px-4 py-2 text-center">
          {f.asOf
            ? `Sync has stopped since ${formatWib(new Date(f.asOf))}. The dashboard team is alerted automatically.`
            : "Sync has not completed yet. Figures fill in as the first sync runs."}
        </div>
      )}
      {f.nightly.missedTwo && (
        <div className="bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 text-[13px] px-4 py-1.5 text-center">
          Some records may be out of date (full check last ran {f.nightly.ageDays} days ago)
        </div>
      )}
    </>
  );
}

const IDLE_MS = 60 * 60_000;
const WARN_MS = 2 * 60_000;

/** "Stay signed in" banner 2 minutes before logout (SPEC §12). */
export function SessionWarning({ me }: { me: Me }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const idleEnd = lastActivity + IDLE_MS;
  const absEnd = new Date(me.absoluteExpiresAt).getTime();
  const end = Math.min(idleEnd, absEnd);
  const left = end - now;
  const expired = left <= 0;
  useEffect(() => {
    if (!expired) return;
    // Ask as a background request, which never extends the session, until the server agrees it
    // has ended (its clock may trail ours by a moment). The 401 sends the user to sign-in.
    const check = () => void api("/me", { background: true }).catch(() => {});
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [expired]);
  if (left > WARN_MS || left <= 0) return null;
  const s = Math.ceil(left / 1000);
  const time = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const absolute = absEnd <= idleEnd;
  return (
    <div role="alert" className="bg-brand-2 text-white text-[13px] px-4 py-2 flex items-center justify-center gap-3">
      <span>
        {absolute ? `Your session reaches its 12-hour limit in ${time}.` : `You will be signed out in ${time} for inactivity.`}
      </span>
      {!absolute && (
        <button className="bg-white text-brand-2 rounded-input px-3 py-0.5 font-semibold" onClick={() => void api("/me").then(() => setNow(Date.now()))}>
          Stay signed in
        </button>
      )}
    </div>
  );
}
